import { Float32BufferAttribute, type BufferGeometry } from 'three';
import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from './meshBuilder';
import { orientedFace } from '../fabric/solids';

export interface ClothSpan {
  readonly start: number;
  readonly end: number;
  readonly anchor: Vec3;
  readonly along: Vec3;
  readonly normal: Vec3;
  readonly length: number;
  readonly amplitude: number;
  readonly phase: number;
}
/** A travelling ripple with slower gusts; shared with the painted vertex shader. */
export function clothWave(time: number, phase: number): number {
  return (
    (Math.sin(time * 2.8 - phase) + 0.35 * Math.sin(time * 4.3 - phase * 0.7)) *
    (0.75 + 0.25 * Math.sin(time * 0.47 + phase * 0.13))
  );
}
/** Attach only cloth vertices. Hoists and every unmarked piece of geometry stay fixed. */
export function withClothWind(
  geometry: BufferGeometry,
  spans: readonly ClothSpan[]
): BufferGeometry {
  if (!spans.length) return geometry;
  const p = geometry.getAttribute('position'),
    wind = new Float32Array(p.count * 4);
  for (const span of spans)
    for (let i = span.start; i < span.end; i++) {
      const distance =
        (p.getX(i) - span.anchor[0]) * span.along[0] +
        (p.getY(i) - span.anchor[1]) * span.along[1] +
        (p.getZ(i) - span.anchor[2]) * span.along[2];
      const free = Math.max(0, Math.min(1, distance / span.length));
      const amplitude = span.amplitude * free * free;
      for (let k = 0; k < 3; k++) wind[i * 4 + k] = span.normal[k]! * amplitude;
      wind[i * 4 + 3] = span.phase + free * 7;
    }
  geometry.setAttribute('aClothWind', new Float32BufferAttribute(wind, 4));
  // The free edge can travel beyond the original static bound.
  geometry.computeBoundingSphere();
  geometry.boundingSphere!.radius += Math.max(...spans.map((s) => s.amplitude)) * 1.35;
  return geometry;
}

/** Closed, subdivided cloth strip. Marks use the same columns so they ripple with the field. */
export function clothStrip(
  builder: MeshBuilder,
  style: VertexStyle,
  from: Vec3,
  to: Vec3,
  depth: number,
  thickness: number,
  cells = 8
): void {
  const columns = Array.from({ length: cells + 1 }, (_, i) => {
    const t = i / cells,
      x = from[0] + (to[0] - from[0]) * t,
      y = from[1] + (to[1] - from[1]) * t,
      z = from[2] + (to[2] - from[2]) * t;
    return [
      [x, y - depth / 2, z - thickness / 2],
      [x, y + depth / 2, z - thickness / 2],
      [x, y + depth / 2, z + thickness / 2],
      [x, y - depth / 2, z + thickness / 2],
    ] as const;
  });
  const frame = { origin: [0, 0, 0] as const, yaw: 0 };
  for (let i = 0; i < cells; i++)
    for (let side = 0; side < 4; side++) {
      const a = columns[i]!,
        b = columns[i + 1]!,
        next = (side + 1) % 4;
      const outward: Vec3 =
        side === 0 ? [0, 0, -1] : side === 1 ? [0, 1, 0] : side === 2 ? [0, 0, 1] : [0, -1, 0];
      orientedFace(builder, style, frame, [a[side]!, b[side]!, b[next]!, a[next]!], outward);
    }
  orientedFace(builder, style, frame, columns[0]!, [-1, 0, 0]);
  orientedFace(builder, style, frame, columns[cells]!, [1, 0, 0]);
}
